import { useState, ChangeEvent, useEffect } from "react";
import Editor from "../../editor/Editor";
import PAGE_QUERY from "./queries/PAGE_QUERY";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Form, Button, FormCheck } from "react-bootstrap";
import EDIT_PAGE_MUTATION from "./queries/EDIT_PAGE_MUTATION";
import Loading from "../../common/Loading";
import PAGES_QUERY from "./queries/PAGES_QUERY";
import { GalleryItem } from "./types/GalleryItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AlertDismissible from "../../common/AlertDismissible";
import { IGallery } from "./types/Gallery";
import Gallery from "./components/Gallery";
import { removeTypename } from "../../utils";

interface GalleryValidity {
  valid: boolean;
  gallery: IGallery;
}

const EditPage = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [published, setPublished]: [boolean | "not-initialized", Function] =
    useState("not-initialized");
  const [galleries, setGalleries]: [IGallery[] | undefined, Function] =
    useState();
  const [parentId, setParentId] = useState("");
  const [success, setSuccess] = useState(false);
  const [validity, setValidity]: [GalleryValidity[] | undefined, Function] =
    useState([]);
  const { id }: { id?: string } = useParams();
  const getEditedGalleryData = () => {
    return galleries
      ?.filter((g: IGallery) => !!g)
      .map((g: any) => {
        if (!g) return null;
        const changed = { ...g };
        changed.id = changed.id?.indexOf("new-") > -1 ? null : changed.id;
        changed.page = data?.page?.id;
        changed.items = changed.items?.map((i: GalleryItem) => {
          const ci = { ...i };
          if (ci.type === "product") {
            (ci.product as any) = ci?.product?.id;
          }
          delete ci.selected;
          return removeTypename(ci);
        });
        return removeTypename(changed);
      });
  };
  const getEditedData = () => {
    const changed = {
      ...data?.page,
      published,
      title,
      body,
      parent: !parentId ? null : parentId,
      galleries: getEditedGalleryData(),
    };
    delete changed.children;
    delete changed.createdAt;
    delete changed.updatedAt;
    return removeTypename(changed);
  };
  const { data, loading } = useQuery(PAGE_QUERY, {
    variables: { pageId: id },
    onCompleted: (res) => {
      setTitle(res.page.title);
      setBody(res.page.body);
    },
  });
  const { data: pagesData, loading: loadingPages } = useQuery(PAGES_QUERY);
  const [editPage, { loading: saving, error }] = useMutation(
    EDIT_PAGE_MUTATION,
    {
      variables: {
        pageData: {
          ...getEditedData(),
        },
      },
      onCompleted: (res) => {
        setSuccess(true);
      },
      refetchQueries: [
        {
          query: PAGE_QUERY,
          variables: { pageId: id },
        },
      ],
      errorPolicy: 'all',
    }
  );
  useEffect(() => {
    const g = data?.page?.galleries;
    if (g?.length && !galleries) {
      setGalleries(data.page.galleries);
    }
    if (data && published === "not-initialized") {
      setPublished(data?.page?.published);
    } else if (!saving && data && data?.page?.published !== published) {
      editPage();
    }
    if (data?.page?.parent?.id) {
      setParentId(data?.page?.parent?.id);
    }
  }, [data, published, editPage, saving, galleries]);
  const isValid = () => {
    const test = validity?.filter((v) => !v.valid);
    return !test?.length;
  };
  const handleIsValid = (galleryValidity: GalleryValidity) => {
    let isNew = true;
    const changed = validity?.map((v) => {
      if (v.gallery.id === galleryValidity.gallery.id) {
        isNew = false;
        return { ...galleryValidity };
      }
      return { ...v };
    });
    if (!changed) return;
    if (isNew) {
      setValidity([...changed, galleryValidity]);
    } else {
      setValidity([...changed]);
    }
  };
  const addNewGallery = () => {
    const gallery = {
      id: `new-${galleries ? galleries.length : 0}`,
      name: "",
      items: [],
    };
    setGalleries(galleries ? [...galleries, gallery] : [gallery]);
    if (!validity) return;
    setValidity([...validity, { valid: false, gallery }]);
  };
  const galleryChange = (gallery: IGallery) => {
    const changed: (IGallery | null)[] = galleries
      ? galleries
          .map((g: IGallery) => {
            let cg: IGallery = { ...g };
            if (cg.id === gallery.id) {
              if (!gallery.name && !gallery.items) {
                const currentValidity = validity.filter(
                  (v) => v?.gallery?.id !== cg.id
                );
                setValidity(currentValidity);
                return null;
              }
              cg = { ...gallery };
              const items = cg?.items ? [...cg.items] : [];
              cg.items = items.filter((i) => i.selected);
            }
            return cg;
          })
          .filter((g) => g)
      : [];

    setGalleries(changed);
  };

  const renderPage = () => {
    if (error) return <div className="mt-4 mb-4">{`Submission error! ${error.message}`} <Link to="/admin">Back</Link></div>;
    return (
      <Form>
        {(loading || loadingPages) && (
          <div className="mt-4 mb-4">
            <Loading />
          </div>
        )}
        {success && (
          <AlertDismissible
            variant="success"
            dismissible
            onClose={() => setSuccess(false)}
            className="mt-4"
          >
            Page Saved.
          </AlertDismissible>
        )}
        {data && published !== "not-initialized" && (
          <Form.Group className="mt-4">
            <Form.Label>
              {published ? "Page Is Published" : "Page Is Hidden"}
            </Form.Label>
            <FormCheck
              type="switch"
              id={`published-${data?.page.id}`}
              checked={published}
              label=""
              aria-label="is published"
              onChange={() => {
                setPublished(!published);
              }}
            />
          </Form.Group>
        )}
        {data && (
          <div className="mt-4">
            {published ? (
              <span>
                Page Link:{" "}
                <FontAwesomeIcon
                  color="#4E6E17"
                  style={{ cursor: "pointer" }}
                  icon="eye"
                  onClick={() => navigate(`/page/${data?.page?.slug}/`)}
                />
              </span>
            ) : (
              <span>
                Preview Page:{" "}
                <FontAwesomeIcon
                  color="#868e96"
                  style={{ cursor: "pointer" }}
                  icon="eye"
                  onClick={() =>
                    navigate(`/page/${data?.page?.slug}/?preview=true`)
                  }
                />
              </span>
            )}
          </div>
        )}
        {data && (
          <Form.Group className="mt-4" id="pageTitleControl">
            <Form.Label>Title</Form.Label>
            <Form.Control
              onChange={(event: ChangeEvent<any>) => {
                setTitle(event.target.value || "");
              }}
              placeholder="Page Title"
              defaultValue={data?.page?.title}
              type="text"
            />
          </Form.Group>
        )}
        {!loading && pagesData && (
          <Form.Group className="mt-4" id="parentControl">
            <Form.Label>Parent Page</Form.Label>
            <Form.Control
              as="select"
              defaultValue={data?.page?.parent?.id}
              onChange={(event: ChangeEvent<any>) => {
                setParentId(event.target.value || "");
              }}
            >
              <option value="">--- Select One ---</option>
              {pagesData.pages.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        )}
        {data?.page && (
          <Editor
            onChange={(content: string) => setBody(content)}
            value={data.page.body}
            className="mt-4"
          />
        )}
        {!loading && (
          <>
            {galleries
              ?.filter((g: IGallery) => !!g)
              .map((g: IGallery) => (
                <Gallery
                  key={g?.id}
                  show={true}
                  gallery={g}
                  handleChange={galleryChange}
                  handleIsValid={handleIsValid}
                />
              ))}
            <div className="mt-4">
              <Button variant="outline-primary" onClick={addNewGallery}>
                Add Gallery <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
          </>
        )}
        <Button
          disabled={saving || loading || !isValid()}
          size="lg"
          variant="primary"
          className="mt-4"
          onClick={() => {
            editPage();
          }}
        >
          Save Changes
        </Button>
      </Form>
    );
  };
  return <div>{renderPage()}</div>;
};

export default EditPage;
